"use client";

import * as React from "react";
import { cn } from "@baluu/ui/lib/utils";
import { Minus, Plus, Lock, Info } from "lucide-react";
import { Button } from "@baluu/ui/components/button";
import { TicketQuantityInput } from "./ticket-quantity-input";
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogPortal,
} from "@baluu/ui/components/dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";
import type { TicketBookingPolicy } from "./utils/types";
import { RenderMarkdown } from "./utils/render-markdown";

type TicketSelectorProps = {
  id: string;
  name: string;
  description?: string;
  price?: number;
  currency?: string;
  quantity: number;
  minQuantity?: number;
  maxQuantity?: number;
  disabled?: boolean;
  soldOut?: boolean;
  onChange: (quantity: number) => void;
  className?: string;
  ticketTypeGroup?: "regular" | "deposit";
  depositAmount?: number;
  fullPrice?: number;
  priceSuffix?: string;
  membershipRequired?: boolean;
  membershipAccessGranted?: boolean;
  membershipsUrl?: string;
  eligibilityMessage?: string;
  policyHint?: string;
  bookingPolicy?: TicketBookingPolicy | null;
  onPolicyClick?: (ticketName: string, policy: TicketBookingPolicy) => void;
};

function formatPrice(price: number, currency: string = "GBP"): string {
  const symbols: Record<string, string> = {
    GBP: "£",
    USD: "$",
    EUR: "€",
  };
  const symbol = symbols[currency] || currency;
  // Price is stored in minor units (cents/pence), divide by 100
  return `${symbol}${(price / 100).toFixed(2)}`;
}

function TicketSelector({
  name,
  description,
  price,
  currency = "GBP",
  quantity,
  minQuantity = 0,
  maxQuantity = 10,
  disabled = false,
  soldOut = false,
  onChange,
  className,
  ticketTypeGroup,
  depositAmount,
  fullPrice,
  priceSuffix,
  membershipRequired = false,
  membershipAccessGranted = false,
  membershipsUrl,
  eligibilityMessage,
  policyHint,
  bookingPolicy,
  onPolicyClick,
}: TicketSelectorProps) {
  const [isPolicyDialogOpen, setIsPolicyDialogOpen] = React.useState(false);
  const isMembersOnlyLocked = membershipRequired && !membershipAccessGranted;
  const isIneligible = !!eligibilityMessage;
  const canDecrease = quantity > 0 && !disabled && !soldOut;
  const canIncrease = quantity < maxQuantity && !disabled && !soldOut;
  const [quantityNote, setQuantityNote] = React.useState<string>();
  const [isAnimating, setIsAnimating] = React.useState<
    "increase" | "decrease" | null
  >(null);

  const handleDecrease = () => {
    if (canDecrease) {
      setIsAnimating("decrease");
      setQuantityNote(undefined);
      const newQuantity = quantity - 1;
      onChange(newQuantity < minQuantity ? 0 : newQuantity);
      setTimeout(() => setIsAnimating(null), 150);
    }
  };

  const handleIncrease = () => {
    if (canIncrease) {
      setIsAnimating("increase");
      // From 0, jump straight to the per-order minimum: 1..min-1 can never be
      // booked, so a "Min. 8" ticket shouldn't take eight taps. Say why the
      // number jumped so it doesn't read as a glitch.
      const next = Math.min(Math.max(quantity + 1, minQuantity), maxQuantity);
      setQuantityNote(
        next > quantity + 1 ? `Minimum ${minQuantity} per order` : undefined,
      );
      onChange(next);
      setTimeout(() => setIsAnimating(null), 150);
    }
  };

  // Format the price display
  const priceDisplay = React.useMemo(() => {
    if (soldOut) return null;

    if (
      ticketTypeGroup === "deposit" &&
      depositAmount !== undefined &&
      fullPrice !== undefined
    ) {
      // Deposit ticket
      if (depositAmount === 0) return "Free";
      return (
        <>
          Deposit: {formatPrice(depositAmount, currency)} / Full:{" "}
          {formatPrice(fullPrice, currency)}
        </>
      );
    }

    if (price !== undefined) {
      return price === 0 ? "Free" : formatPrice(price, currency);
    }

    return null;
  }, [soldOut, ticketTypeGroup, depositAmount, fullPrice, price, currency]);

  return (
    <div
      className={cn(
        "p-4 bg-card rounded-lg border border-border",
        "transition-shadow duration-200",
        quantity > 0 && "border-primary/30 shadow-sm",
        (disabled || isMembersOnlyLocked || isIneligible) && "opacity-50",
        className,
      )}
    >
      {/* Mobile: Stacked layout / Desktop: Side by side */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        {/* Ticket Info */}
        <div className="flex-1 min-w-0">
          {/* Row 1: Name + Sold out badge + Members only badge */}
          <div className="flex items-center gap-2 flex-wrap">
            {isMembersOnlyLocked && <Lock className="h-4 w-4 text-muted-foreground" />}
            <h4 className="font-medium text-foreground">{name}</h4>
            {membershipRequired && (
              <span className={cn(
                "text-xs font-medium px-2 py-0.5 rounded",
                membershipAccessGranted
                  ? "bg-primary/10 text-primary"
                  : "bg-muted text-muted-foreground"
              )}>
                {membershipAccessGranted ? "Member" : "Members Only"}
              </span>
            )}
            {soldOut && (
              <span className="text-xs font-medium text-destructive-foreground bg-destructive/10 px-2 py-0.5 rounded">
                Sold out
              </span>
            )}
          </div>

          {/* Row 2: Price (prominent on mobile) */}
          {priceDisplay && (
            <p className="text-base font-semibold text-primary mt-1">
              {priceDisplay}
              {priceSuffix && (
                <span className="text-xs font-normal text-muted-foreground ml-1">
                  {priceSuffix}
                </span>
              )}
            </p>
          )}

          {/* Row 3: Description */}
          {description && (
            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
              {description}
            </p>
          )}

          {/* Row 4: Cancellation Policy button */}
          {bookingPolicy && !isIneligible && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (onPolicyClick) {
                  onPolicyClick(name, bookingPolicy);
                } else {
                  setIsPolicyDialogOpen(true);
                }
              }}
              className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 font-medium mt-1 text-left transition-colors"
            >
              Cancellation Policy
            </button>
          )}

          {/* Row 5: Eligibility message */}
          {isIneligible && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 flex items-center gap-1">
              <Info className="h-3 w-3 shrink-0" />
              {eligibilityMessage}
            </p>
          )}
        </div>

        {/* Members Only - Become a Member CTA */}
        {isMembersOnlyLocked && membershipsUrl && (
          <div className="pt-2 md:pt-0">
            <a
              href={membershipsUrl}
              className="text-sm text-primary hover:underline font-medium"
            >
              Become a Member →
            </a>
          </div>
        )}

        {/* Quantity Controls */}
        {!soldOut && !isMembersOnlyLocked && !isIneligible && (
          <div className="flex items-center justify-between md:justify-end gap-3 pt-2 md:pt-0 border-t md:border-t-0 border-border/50">
            {/* Mobile: Show "Quantity" label */}
            <span className="text-sm text-muted-foreground md:hidden">
              Quantity
            </span>

            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="outline"
                size="icon-sm"
                onClick={handleDecrease}
                disabled={!canDecrease}
                className={cn(
                  "rounded-full min-w-[44px] min-h-[44px] touch-manipulation",
                  "transition-all duration-150 ease-out",
                  canDecrease && "hover:scale-110 active:scale-95",
                  isAnimating === "decrease" && "scale-90",
                )}
              >
                <Minus className="w-4 h-4" />
              </Button>

              <TicketQuantityInput
                ticketName={name}
                quantity={quantity}
                minQuantity={minQuantity}
                maxQuantity={maxQuantity}
                canIncrease={canIncrease}
                canDecrease={canDecrease}
                onChange={onChange}
                onNote={setQuantityNote}
              />

              <Button
                type="button"
                variant="outline"
                size="icon-sm"
                onClick={handleIncrease}
                disabled={!canIncrease}
                className={cn(
                  "rounded-full min-w-[44px] min-h-[44px] touch-manipulation",
                  "transition-all duration-150 ease-out",
                  canIncrease && "hover:scale-110 active:scale-95",
                  isAnimating === "increase" && "scale-90",
                )}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {quantityNote && (
        <p
          aria-live="polite"
          className="mt-2 text-xs text-muted-foreground md:text-right"
        >
          {quantityNote}
        </p>
      )}

      {/* Per-ticket policy dialog (only when not using external portal) */}
      {bookingPolicy && !onPolicyClick && (
        <TicketPolicyDialog
          isOpen={isPolicyDialogOpen}
          onClose={() => setIsPolicyDialogOpen(false)}
          ticketName={name}
          policy={bookingPolicy}
        />
      )}
    </div>
  );
}

export function TicketPolicyDialog({
  isOpen,
  onClose,
  ticketName,
  policy,
}: {
  isOpen: boolean;
  onClose: () => void;
  ticketName: string;
  policy: TicketBookingPolicy;
}) {
  const sortedTiers = policy.cancellationTiers
    ? [...policy.cancellationTiers].sort((a, b) => {
        if (a.daysBefore === b.daysBefore) return b.hoursBefore - a.hoursBefore;
        return b.daysBefore - a.daysBefore;
      })
    : [];

  const rescheduleParts: string[] = [];
  if (policy.rescheduleDaysBefore > 0) {
    rescheduleParts.push(
      policy.rescheduleDaysBefore > 1
        ? `${policy.rescheduleDaysBefore} days`
        : "1 day",
    );
  }
  if (policy.rescheduleHoursBefore > 0) {
    rescheduleParts.push(
      policy.rescheduleHoursBefore > 1
        ? `${policy.rescheduleHoursBefore} hours`
        : "1 hour",
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogPortal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-[9998] bg-black/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <DialogPrimitive.Content
          className="fixed top-[50%] left-[50%] z-[9999] w-full max-w-md translate-x-[-50%] translate-y-[-50%] bg-background rounded-lg border shadow-2xl !p-0 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <DialogPrimitive.Close className="absolute top-4 right-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 z-10">
            <XIcon className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>

          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle>Cancellation Policy</DialogTitle>
            <p className="text-sm text-muted-foreground">{ticketName} &mdash; {policy.name}</p>
          </DialogHeader>

          <div className="max-h-[60vh] overflow-y-auto overscroll-contain">
            <div className="space-y-4 px-6 py-4">
              {/* Cancellation */}
              {!policy.canCancel ? (
                <p className="text-sm text-muted-foreground">No cancellation allowed.</p>
              ) : (
                <div className="text-sm text-muted-foreground">
                  <p className="font-medium text-foreground text-xs uppercase tracking-wide mb-1">Cancellation</p>
                  <ul className="space-y-0.5">
                    {sortedTiers.map((tier, index) => {
                      const daysText = tier.daysBefore > 0
                        ? `${tier.daysBefore} ${tier.daysBefore > 1 ? "days" : "day"}`
                        : null;
                      const hoursText = tier.hoursBefore > 0
                        ? `${tier.hoursBefore} ${tier.hoursBefore > 1 ? "hours" : "hour"}`
                        : null;
                      const timeText = [daysText, hoursText].filter(Boolean).join(" ");
                      return (
                        <li key={index}>
                          {timeText} before: {tier.refundPercentage}% refund
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* Reschedule */}
              {!policy.canReschedule ? (
                <p className="text-sm text-muted-foreground">No reschedule allowed.</p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground text-xs uppercase tracking-wide">Reschedule: </span>
                  Up to {rescheduleParts.join(" and ") || "24 hours"} before the event.
                </p>
              )}

              {/* Additional terms */}
              {policy.additionalTerms && (
                <div className="text-sm">
                  <RenderMarkdown content={policy.additionalTerms} />
                </div>
              )}
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}

type TicketSelectorGroupProps = {
  children: React.ReactNode;
  className?: string;
};

function TicketSelectorGroup({
  children,
  className,
}: TicketSelectorGroupProps) {
  return <div className={cn("space-y-3", className)}>{children}</div>;
}

TicketSelector.Group = TicketSelectorGroup;

export { TicketSelector };
